import { JSONSchema7 } from 'json-schema';
import { extractMixins, mixinToNormalForm } from '../../../mixins';
import {
  anchorDefinition,
  ANSWER_OPERATIONS,
  imageGallerySchema,
  imageSchema,
  mergeObjectSchemas,
  NUMBER_OPERATIONS,
  TEXT_OPERATIONS,
} from './questionSchemaFields';

export const ANSWERS_LOGIC_OPERATIONS = [...TEXT_OPERATIONS, ...ANSWER_OPERATIONS, ...NUMBER_OPERATIONS] as const;

export const answerAwardAmount = mixinToNormalForm({
  awardAmount: {
    type: 'number',
    description: 'Вознаграждение, целое число',
  },
} as const);

export const answerMarkerImage = mixinToNormalForm({
  markerImage: {
    type: 'string',
    description: 'Ссылка на изображение используемое в списке как bullet с фиксированным размером',
  },
} as const);

export const answerCorrectRank = mixinToNormalForm({
  correctRank: { type: 'number' },
} as const);

export const answerImage = mixinToNormalForm({
  image: imageSchema,
  imageBackground: { type: 'string' },
} as const);

export const answerExclusive = {
  type: 'object',
  properties: {
    exclusiveAnswer: { type: 'boolean' },
  },
} as const;

export const answerOther = {
  type: 'object',
  properties: {
    otherAnswer: { type: 'boolean' },
  },
} as const;

export const answerImageGallery = {
  type: 'object',
  properties: {
    image: imageGallerySchema,
    imageBackground: { type: 'string' },
  },
  required: ['image'],
} as const;

export const answerFileList = {
  type: 'object',
  properties: {
    fileSrc: { type: 'string' },
    fileName: { type: 'string' },
  },
  required: ['fileSrc'],
} as const;

export const answerIsCorrect = mixinToNormalForm({
  isCorrect: { type: 'boolean' },
} as const);

export const answerIsCorrectWithHint = {
  type: 'object',
  properties: {
    isCorrect: { type: 'boolean' },
  },
  if: {
    properties: { isCorrect: { const: true } },
    required: ['isCorrect'],
  },
  then: {
    properties: {
      successHint: { type: 'string' },
    },
  },
  else: {
    properties: {
      errorHint: { type: 'string' },
    },
  },
} as const;

export const answerIsCorrectWithHintAndImage = {
  type: 'object',
  properties: {
    isCorrect: { type: 'boolean' },
    feedbackImage: {
      oneOf: [imageSchema, { type: 'null' }],
    },
  },
  if: {
    properties: { isCorrect: { const: true } },
  },
  then: {
    properties: {
      successHint: { type: 'string' },
    },
  },
  else: {
    properties: {
      errorHint: { type: 'string' },
    },
  },
  required: ['isCorrect'],
} as const;

export const answerIsCorrectWithHints = {
  ...answerIsCorrectWithHint,
  else: {
    ...answerIsCorrectWithHint.else,
    properties: {
      ...answerIsCorrectWithHint.else.properties,
      errorHint2: { type: 'string' },
    },
  },
} as const;

export const answerIsCorrectWithHintsAndImages = {
  ...answerIsCorrectWithHintAndImage,
  else: {
    ...answerIsCorrectWithHintAndImage.else,
    properties: {
      ...answerIsCorrectWithHintAndImage.else.properties,
      errorHint2: { type: 'string' },
    },
  },
} as const;

export const answerTextPosition = mixinToNormalForm({
  textPosition: { enum: ['BEFORE_MEDIAFILE', 'AFTER_MEDIAFILE'] },
} as const);

export const answerLogicRules = mixinToNormalForm({
  logicOperationType: {
    enum: ['OR', 'AND'],
  },
  logicRules: {
    type: 'array',
    items: { $ref: '#/definitions/logicRule' },
  },
} as const);

export function createAnswerSchema<T extends Record<string, unknown>[]>(...schemas: T) {
  return mergeObjectSchemas(
    {
      type: 'object',
      properties: {
        id: { type: 'string' },
        text: { type: 'string' },
      },
      unevaluatedProperties: false,
      required: ['id'],
    } as const,
    ...schemas,
  );
}

export function createAnswerSchemaWithMixins({
  mixins = {},
  base,
}: {
  mixins?: Record<string, JSONSchema7>;
  base?: JSONSchema7;
} = {}) {
  const localMixins = {
    answerBase: {
      type: 'object',
      properties: {
        id: { type: 'string' },
        deckId: { type: 'string' },
        text: { type: 'string' },
        ...anchorDefinition,
      },
      required: ['id'],
    },
  } satisfies Record<string, JSONSchema7>;
  const mergedMixins = { ...mixins, ...localMixins };
  return {
    type: 'object',
    allOf: [...(base ? [base] : []), ...Object.keys(mergedMixins).map(v => ({ $ref: `#/definitions/${v}` }))],
    definitions: {
      ...extractMixins(Object.values(mixins)),
      ...mergedMixins,
    } as any,
    unevaluatedProperties: false,
  } as const;
}
